import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getLatestActivities, getAllUsers } from '../../services/databaseService';
import './Activity.css';
import UserAvatar from '../../components/UserAvatar/UserAvatar';
import { useNavigate } from 'react-router-dom';

function Activity() {
  const [activities, setActivities] = useState([]);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Get all users to map IDs to names
        const allUsers = await getAllUsers();
        const usersMap = allUsers.reduce((acc, user) => {
          acc[user.uid] = user;
          return acc;
        }, {});
        setUsers(usersMap);

        // Get all activities
        const activitiesData = await getLatestActivities();
        
        // Flatten and sort activities
        const flattenedActivities = Object.values(activitiesData)
          .flat()
          .sort((a, b) => b.timestamp?.seconds - a.timestamp?.seconds);

        setActivities(flattenedActivities);
      } catch (error) {
        console.error('Error fetching activities:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleShowClick = (showId) => {
    navigate(`/show/${showId}`, {
      state: { from: 'activity' }
    });
  };

  const formatActivityText = (activity) => {
    const userName = users[activity.userId]?.displayName?.split(' ')[0] || 'Någon';
    const showTitle = (
      <span 
        className="activity-show-title"
        onClick={(e) => {
          e.stopPropagation();
          handleShowClick(activity.showId);
        }}
      >
        {activity.showTitle}
      </span>
    );
    
    switch (activity.action) {
      case 'rating':
        return <>{userName} gav {showTitle} {activity.value} smultron</>;
      
      case 'status':
        const statusText = {
          'watching': 'Tittar på',
          'watchlist': 'Ska se',
          'seen': 'Har sett',
          'archived': 'Arkiverade'
        }[activity.value] || activity.value;
        return <>{userName} markerade {showTitle} som "{statusText}"</>;
      
      case 'streaming':
        return <>{userName} la till {activity.value} som streamingtjänst för {showTitle}</>;
      
      case 'update':
        if (activity.changes?.status) {
          const newStatus = {
            'watching': 'Tittar på',
            'watchlist': 'Ska se',
            'seen': 'Har sett',
            'archived': 'Arkiverade'
          }[activity.changes.status] || activity.changes.status;
          return <>{userName} ändrade status på {showTitle} till "{newStatus}"</>;
        }
        if (activity.changes?.rating) {
          return <>{userName} ändrade betyg på {showTitle} till {activity.changes.rating} smultron</>;
        }
        return <>{userName} uppdaterade {showTitle}</>;

      default:
        console.log('Unhandled activity action:', activity.action);
        return <>{userName} interagerade med {showTitle}</>;
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate(); // Convert Firestore timestamp to JS Date
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    // If less than 24 hours ago, show relative time
    if (diffInHours < 24) {
      if (diffInHours < 1) {
        const minutes = Math.floor((now - date) / (1000 * 60));
        return `${minutes} min sedan`;
      }
      return `${Math.floor(diffInHours)} tim sedan`;
    }
    
    // If this year, show date and month
    if (date.getFullYear() === now.getFullYear()) {
      return date.toLocaleDateString('sv-SE', { 
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    
    // If different year, include year
    return date.toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="activity-loading">Laddar aktiviteter...</div>;
  }

  return (
    <div className="activity-container">
      <h1>Aktivitet</h1>
      <div className="activity-list">
        {activities.map((activity) => (
          <div key={activity.id} className="activity-item">
            <div className="activity-avatar" onClick={() => navigate(`/profile/${activity.userId}`)}>
              <UserAvatar 
                user={users[activity.userId]}
                size="small"
              />
            </div>
            <div className="activity-content">
              <span className="activity-text">
                {formatActivityText(activity)}
              </span>
              <span className="activity-time">
                {formatTimestamp(activity.timestamp || activity.createdAt)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Activity; 